// PATH: src/components/settings/rolePrompts.ts
/**
 * Le prompt de base d'un rôle, tel que son service le rend : partagé par les
 * Réglages Tilmid+ (cartes par agent) et les deux écrans d'essais, pour que le
 * dirigeant voie partout ce que sa consigne complète.
 */
import type { FactPromptsResponse, LlmRole, PromptGroup, QuestPromptsResponse } from "../../models/domain";

export interface RolePrompts {
  version?: string;
  assembly?: string[];
  groups: PromptGroup[];
  instructionsHeader?: string;
  error?: string | null;
}

/** Les rôles dont le prompt de base vient de tc_embed_be (la rédaction : publiée par tc_docsourcing, servie par lui). */
export type EmbedRole = "objective_extraction" | "fact_extraction" | "fact_review" | "course_writing";

/** Le prompt de base d'un rôle d'objectifs ou de faits (tc_embed_be). Les objectifs ont trois prompts, chacun versionné. */
export const embedRolePrompts = (role: EmbedRole, facts: FactPromptsResponse | null, error: string | null): RolePrompts => {
  if (role === "course_writing") {
    return externalRolePrompts(role, facts, error);
  }
  const r = facts?.roles[role];
  return {
    version: r?.version,
    groups: r ? [{ id: role, label: "", prompts: r.prompts }] : [],
    instructionsHeader: facts?.instructions_header,
    error: error ?? (facts ? (r ? null : "Prompts indisponibles : tc_embed_be d'avant ce rôle.") : "Chargement…"),
  };
};

/**
 * Le prompt de base d'un rôle dont le code vit hors du stack : la dernière
 * publication de l'outil (tc_docsourcing, `chapitre prompts`), avec sa date.
 */
const externalRolePrompts = (role: EmbedRole, facts: FactPromptsResponse | null, error: string | null): RolePrompts => {
  const r = facts?.external?.find((p) => p.role === role);
  const published = r ? new Date(r.published_at).toLocaleDateString("fr-FR") : "";
  return {
    version: r ? `${r.version} (publiée le ${published})` : undefined,
    groups: r ? [{ id: role, label: "", prompts: r.prompts }] : [],
    instructionsHeader: facts?.instructions_header,
    error:
      error ??
      (facts
        ? r
          ? null
          : "Pas encore publiés : ils le sont par l'atelier tc_docsourcing (python -m docsource chapitre prompts)."
        : "Chargement…"),
  };
};

/** Les prompts de base d'un rôle, depuis la réponse de son service. */
export const rolePrompts = (
  role: LlmRole,
  facts: FactPromptsResponse | null,
  quests: QuestPromptsResponse | null,
  errors: { facts: string | null; quests: string | null },
): RolePrompts => {
  if (role === "objective_extraction" || role === "fact_extraction" || role === "fact_review" || role === "course_writing") {
    return embedRolePrompts(role, facts, errors.facts);
  }
  const r = quests?.roles[role];
  return {
    version: r?.version,
    assembly: r?.assembly,
    groups: r?.groups ?? [],
    instructionsHeader: quests?.instructions_header,
    error: errors.quests ?? (quests ? null : "Chargement…"),
  };
};

/**
 * Les versions du prompt de base qu'un essai a réellement utilisées, sans
 * doublon ni vide, dans l'ordre de première apparition : un essai lancé de
 * part et d'autre d'une livraison du prompt en porte deux.
 */
export const versionsUsed = (versions: (string | null | undefined)[]): string[] => [
  ...new Set(versions.filter((v): v is string => Boolean(v))),
];
